import * as yup from 'yup';
import { toDateInputValue } from '../../../library/dateTimeHelper';
import { DISCOUNT_PERCENT_BOUNDS, integerIn, TranslateFn } from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';

/**
 * What the add-a-service form holds while it is being filled in.
 *
 * The numbers are strings, because that is what an `<input>` gives back and coercing per
 * keystroke is what makes a field impossible to clear -- the same reason the evaluation
 * wizard keeps its numbers as strings. `toCreatePurchaseRequest` converts once, on
 * submit.
 *
 * There is no `finalPrice`. What the patient pays is `listPrice` less the discount, which
 * is arithmetic rather than an answer -- `priceBreakdown` derives it for the field that
 * displays it and for the amount that is sent, so the two cannot disagree. "Call it 900" is
 * said by editing the list price, which is the figure the clinic is free to move.
 */
export interface AddPurchaseFormValues {
    serviceId: string;
    /** Who will actually give the sessions. Defaults to the signed-in therapist. */
    therapistId: string;
    totalSessions: string;
    listPrice: string;
    discountPercent: string;
    /**
     * The day the sale was made. Only one of this and `validUntil` is ever shown, and only
     * the shown one is sent -- see the dialog.
     */
    soldOn: string;
    /**
     * Whether this sale is also the record of the session, rather than only the sale.
     *
     * Ticked, `providedAtTime` is sent and the API writes the session into the diary as
     * held. Unticked is the other real case: the session is already booked in, so the
     * appointment exists and will name this purchase when it is marked held -- writing a
     * second one would double-count it, and could not be written anyway, since the booking
     * already occupies the hour.
     */
    sessionHeld: boolean;
    /** "13:30". Only asked for, and only sent, when `sessionHeld` is ticked. */
    providedAtTime: string;
    validUntil: string;
    notes: string;
}

const NOTES_MAX = 2000;

/**
 * Deliberately thin. The server recomputes the price from the list price and the
 * discount and re-checks every bound, so this exists to answer the therapist before they
 * press the button -- not to be the guard.
 */
export const createAddPurchaseSchema = (translate: TranslateFn) => yup.object({
    serviceId: yup.string().required(translate(ResourcesIds.ADD_PURCHASE_SERVICE_REQUIRED)),

    therapistId: yup.string().required(translate(ResourcesIds.ADD_PURCHASE_THERAPIST_REQUIRED)),

    totalSessions: yup.string().defined()
        .test('at-least-one', translate(ResourcesIds.ADD_PURCHASE_SESSIONS_MIN),
            value => Number(value) >= 1),

    listPrice: yup.string().defined()
        .test('not-negative', translate(ResourcesIds.ADD_PURCHASE_PRICE_INVALID),
            value => value !== '' && Number(value) >= 0),

    // Bounded at both ends from the one declaration the input's own spinner reads, so the
    // browser and the schema cannot come to disagree about what a discount is.
    discountPercent: integerIn(
        DISCOUNT_PERCENT_BOUNDS, translate, ResourcesIds.ADD_PURCHASE_DISCOUNT_RANGE),

    /*
     * Defaulted rather than merely `defined`, because only one of `soldOn` and `validUntil`
     * is ever on screen and react-hook-form drops a field's value once the control that
     * owned it unmounts. Picking a package after the dialog opened would otherwise leave
     * `soldOn` undefined, failing `defined()` on a field nobody can see -- a Save button
     * disabled with no message anywhere. Neither value is sent for the kind that hides it.
     */
    soldOn: yup.string().defined().default(''),

    sessionHeld: yup.boolean().defined(),

    // Required only while the field is on screen. The dialog unticks the box whenever a
    // package is chosen, so this never demands a time nobody can see.
    providedAtTime: yup.string().defined()
        .when('sessionHeld', {
            is: true,
            then: schema => schema.required(translate(ResourcesIds.ADD_PURCHASE_TIME_REQUIRED)),
        }),

    /**
     * Defaulted for the same reason as `soldOn` above: it unmounts on the other branch.
     *
     * Empty is the ordinary case -- a package with no expiry never stops being usable --
     * so the bound only speaks about a date that was actually typed. A package is bought
     * now and drawn down over the weeks after, which makes an expiry already behind us a
     * package dead on arrival: the API dates it today and then refuses it for expiring
     * before it was made. Caught here so the therapist is told on the field rather than by
     * a banner after a failed save.
     *
     * `today` is read inside the test rather than closed over when the schema is built:
     * the dialog memoises the schema, and a clinic that leaves it open across midnight
     * would otherwise go on validating against yesterday.
     */
    validUntil: yup.string().defined().default('')
        .test('not-already-expired', translate(ResourcesIds.ADD_PURCHASE_VALID_UNTIL_PAST),
            // Zero-padded ISO dates compare lexicographically, which is why this needs no
            // Date on either side -- the same reason the booking screen's week strip does
            // its comparisons on strings.
            value => !value || value >= toDateInputValue(new Date())),

    notes: yup.string().defined()
        .max(NOTES_MAX, translate(ResourcesIds.CLINIC_FIELD_TOO_LONG, { max: NOTES_MAX })),
});
